import { type AncestorNode, Block } from '@gitbook/slate';
import type { List } from '@gitbook/util-immutable';

import { createCodeBlock, getCodeText } from '../code';
import { BLOCK_CODE, BLOCK_PROMPT, type BlockPromptData } from '../types';

/**
 * Create a prompt block.
 */
export function createPrompt(props: Partial<BlockPromptData> = {}): Block {
    const data: BlockPromptData = {
        description: props.description ?? '',
        ...(props.icon !== undefined ? { icon: props.icon } : {}),
        ...(props.openInAIProviders !== undefined
            ? { openInAIProviders: props.openInAIProviders }
            : {}),
        ...(props.defaultExpanded !== undefined ? { defaultExpanded: props.defaultExpanded } : {}),
    };

    return Block.create({
        type: BLOCK_PROMPT,
        data,
        nodes: [createCodeBlock('// add agent instructions', { syntax: 'markdown' })],
    });
}

/**
 * Check whether a node is nested anywhere inside a prompt block.
 */
export function hasPromptAncestor(ancestors: List<AncestorNode> | null | undefined): boolean {
    return (
        ancestors?.some((ancestor) => Block.isBlock(ancestor) && ancestor.type === BLOCK_PROMPT) ??
        false
    );
}

/**
 * Return the prompt block that contains the current node, when nested in one.
 */
export function getPromptAncestor(ancestors: List<AncestorNode> | null | undefined): Block | null {
    const promptAncestor = ancestors?.find(
        (ancestor) => Block.isBlock(ancestor) && ancestor.type === BLOCK_PROMPT
    );

    return Block.isBlock(promptAncestor) ? promptAncestor : null;
}

/**
 * Return the code block contained in a prompt block.
 */
export function getPromptCodeBlock(block: Block): Block | null {
    const codeBlock = block.findDescendant(
        (node) => Block.isBlock(node) && node.type === BLOCK_CODE
    );

    return Block.isBlock(codeBlock) ? codeBlock : null;
}

/**
 * Return the prompt text from its code block, as it reads on the given side of a diff.
 */
export function getPromptText(
    block: Block,
    options: { diffSide?: 'before' | 'after' } = {}
): string | null {
    const codeBlock = getPromptCodeBlock(block);
    return codeBlock ? getCodeText(codeBlock, options) : null;
}
